import React, { useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import SlideOver from "../../tailwindUI/SlideOver";
import SecondaryLoader from "../../hooks/SecondaryLoader";
import { AlertContext } from "../../context/AlertContext";
import { getPaymentLinks } from "../../apiClient/operations/paymentLinksOperations";
import PrimaryButton from "../../tailwindUI/PrimaryButton";
import { PlusIcon } from "@heroicons/react/24/outline";
import { FormInput } from "../FormInput";
import useFormatDate from "../../hooks/useFormatDate";
import { useForm } from "react-hook-form";
import LongAlert from "../../tailwindUI/LongAlert";
import { putParameterStore } from "../../apiClient/operations/ssmOperations";

import { DateTimeInput } from "../../tailwindUI/DateTimeInput";
import { cn } from "../../util";

const defaultFormValues = {
  id: "",
  name: "",
  from_date: "",
  to_date: "",
  expires_at: "",
};

export function AgentPaymentLinkSlideForm({
  openSlideForm,
  setOpenSlideForm,
  linkToUpdate = null,
  linksParams = [],
  setLinksParams,
}) {
  const { setAlert } = useContext(AlertContext);
  const { formatDate } = useFormatDate();
  const [isLoading, setIsLoading] = useState(false);
  const [isPending, setIsPending] = useState(false);
  const [error, setError] = useState(null);
  const scrollRef = useRef(null);

  const {
    register,
    reset,
    getValues,
    setValue,
    handleSubmit,
    watch,
    trigger,
    clearErrors,
    formState: { errors },
  } = useForm({
    values: linkToUpdate || defaultFormValues,
    mode: "onTouched",
  });

  const [paymentLinksAvailable, setPaymentLinksAvailable] = useState([]);
  const [selectedPaymentLink, setSelectedPaymentLink] = useState(null);
  const [optionsPaymentLinks, setOptionsPaymentLinks] = useState([]);
  const disableForm = optionsPaymentLinks.length === 0 && !linkToUpdate;

  const selectedId = watch("id");
  const selectedFromDate = watch("from_date");
  const selectedToDate = watch("to_date");

  const handleScroll = () => {
    scrollRef.current?.scrollTo({
      top: 0,
      behavior: "auto",
    });
  };

  const onSubmit = async (data) => {
    try {
      setIsPending(true);
      const newLlinks = linkToUpdate
        ? linksParams.map((link) => (link.id === linkToUpdate.id ? data : link))
        : linksParams.concat(data);

      await putParameterStore("fxt-api-payment-ulinks-settings", {
        universal_payment_links: newLlinks,
      });

      setLinksParams(newLlinks);

      onClose();
      setAlert({
        active: true,
        type: "success",
        message: linkToUpdate
          ? "Link de pago actualizado correctamente"
          : "Link de pago creado correctamente",
      });
    } catch (error) {
      handleScroll();
      setError(error?.message || "Error al procesar tu solicitud");
    } finally {
      setIsPending(false);
    }
  };

  const onClose = () => {
    if (!linkToUpdate) {
      reset(defaultFormValues);
    }
    clearErrors();
    setOpenSlideForm(false);
  };

  useEffect(() => {
    clearErrors();
  }, [linkToUpdate]);

  useEffect(() => {
    const fetchPaymentLinks = async () => {
      try {
        setIsLoading(true);
        const response = await getPaymentLinks(
          1,
          999,
          "",
          "",
          " $AND type=universal_payment_link $AND status=pending"
        );
        setPaymentLinksAvailable(response?.data || []);
      } catch (error) {
        setError(
          error?.message || "Error al obtener links de pago disponibles"
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchPaymentLinks();
  }, []);

  useEffect(() => {
    const linkParamIds = linksParams.map((link) => link.id);
    let filteredLinks = paymentLinksAvailable;
    if (!linkToUpdate) {
      filteredLinks = paymentLinksAvailable.filter((link) => {
        const isInUse = linkParamIds.includes(link.id);
        return !isInUse;
      });
    }
    const optionsPaymentLinks = filteredLinks.map((link) => ({
      label: `${link.name}`,
      value: link.id,
    }));
    setOptionsPaymentLinks(optionsPaymentLinks);
    if (filteredLinks.length === 0) {
      setError("No hay links de pago disponibles para asignar.");
    } else {
      setError(null);
    }
  }, [linkToUpdate, linksParams, paymentLinksAvailable]);

  useEffect(() => {
    const selectedPaymentLink = paymentLinksAvailable.find(
      (link) => link.id === getValues("id")
    );
    setValue(
      "expires_at",
      selectedPaymentLink ? selectedPaymentLink?.expires_at : ""
    );
    setValue("name", selectedPaymentLink ? selectedPaymentLink?.name : "");
    setSelectedPaymentLink(selectedPaymentLink);
  }, [selectedId, linkToUpdate]);

  useEffect(() => {
    if (selectedFromDate) {
      trigger("from_date"); // validate
    }
  }, [selectedFromDate]);

  useEffect(() => {
    if (selectedToDate) {
      trigger("to_date"); // validate
    }
  }, [selectedToDate]);

  return (
    <SlideOver
      ref={scrollRef}
      open={openSlideForm}
      setOpen={setOpenSlideForm}
      onClose={onClose}
      title={
        linkToUpdate ? "Actualizar link universal" : "Agregar link universal"
      }
      className="max-h-screen"
    >
      {isPending && <SecondaryLoader />}
      <form
        onSubmit={handleSubmit(onSubmit)}
        className="w-full h-full flex flex-col gap-2"
      >
        {error && (
          <div className="w-full sticky mb-24 z-[30]">
            <LongAlert
              className="px-0"
              title={error}
              show={error !== null}
              onClose={() => setError(null)}
              withCloseButton={disableForm ? false : true}
              alwaysShow
            />
          </div>
        )}
        <div className="">
          <FormInput
            name="id"
            variant="select"
            label="Link de pago"
            options={optionsPaymentLinks}
            errors={errors}
            register={register}
            isLoading={isLoading}
            disabled={isLoading || disableForm || linkToUpdate}
            rules={{
              required: "*El link de pago es obligatorio",
              validate: () => {
                if (optionsPaymentLinks.length === 0)
                  return "*No hay links de pago disponibles";
                return true;
              },
            }}
          />
          <div>
            <label
              htmlFor="link_id"
              className="block text-sm font-medium text-gray-500"
            >
              ID
            </label>
            <input
              id="link_id"
              type="text"
              placeholder="ID del link de pago"
              value={selectedPaymentLink?.id || ""}
              className={cn(
                "font-normal border-v2-gray-border-tables text-v2-text-bar-steps focus:ring-v2-blue-text-login focus:border-v2-blue-text-login transition-all block w-full px-4 my-1 rounded-md disabled:cursor-not-allowed read-only:text-gray-500",
                errors.id && "border-red-500"
              )}
              readOnly
              disabled
            />
            <p
              role="alert"
              className={cn(
                "mt-1 text-xs text-red-500",
                errors.id ? "visible" : "invisible"
              )}
            >
              *Selecciona un link de pago
            </p>
          </div>
          <div>
            <label
              htmlFor="expires_at"
              className="block text-sm font-medium text-gray-500"
            >
              Fecha de expiración
            </label>
            <input
              id="expires_at"
              type="text"
              placeholder="dd/mm/yyyy, hh:mm:ss"
              value={
                selectedPaymentLink
                  ? formatDate(
                      selectedPaymentLink?.expires_at,
                      "DD/MM/YYYY, hh:mm:ss A"
                    )
                  : ""
              }
              className={cn(
                "font-normal border-v2-gray-border-tables text-v2-text-bar-steps focus:ring-v2-blue-text-login focus:border-v2-blue-text-login transition-all block w-full px-4 my-1 rounded-md disabled:cursor-not-allowed read-only:text-gray-500",
                errors.id && "border-red-500"
              )}
              readOnly
              disabled
            />
            <p
              role="alert"
              className={cn(
                "mt-1 text-xs text-red-500",
                errors.id ? "visible" : "invisible"
              )}
            >
              *Selecciona un link de pago
            </p>
          </div>
        </div>
        <div className="bg-gray-200 h-[1.5px] mb-2" />
        <DateTimeInput
          name="from_date"
          className=""
          label="Fecha y hora de inicio"
          timeLabel="Hora de inicio"
          placeholder="dd/mm/yyyy, hh:mm:ss"
          errors={errors}
          register={register}
          rules={{
            required: "*La fecha de inicio es obligatoria",
            validate: () => {
              for (const link of linksParams) {
                if (
                  selectedId !== link.id &&
                  selectedFromDate >= link.from_date &&
                  selectedFromDate <= link.to_date
                ) {
                  return "*La fecha de inicio no está disponible porque se superpone con otro link de pago existente";
                }
              }
              return true;
            },
          }}
          value={selectedFromDate}
          setValue={setValue}
          position="bottom"
          minimumDate={new Date()}
          maximumDate={
            selectedPaymentLink
              ? new Date(selectedPaymentLink?.expires_at)
              : null
          }
          disabled={disableForm}
        />
        <DateTimeInput
          name="to_date"
          className=""
          label="Fecha y hora de finalización"
          timeLabel="Hora de finalización"
          placeholder="dd/mm/yyyy, hh:mm:ss"
          errors={errors}
          register={register}
          value={selectedToDate}
          setValue={setValue}
          position="bottom"
          rules={{
            required: "*La fecha de finalización es obligatoria",
            validate: () => {
              if (selectedToDate <= selectedFromDate) {
                return "*La fecha final debe ser posterior a la fecha inicial";
              } else if (selectedPaymentLink?.expires_at < selectedToDate) {
                return "*La fecha final debe ser anterior a la fecha de expiración del link de pago";
              }
              for (const link of linksParams) {
                if (
                  selectedId !== link.id &&
                  selectedToDate >= link.from_date &&
                  selectedToDate <= link.to_date
                ) {
                  return "*La fecha final no está disponible porque se superpone con otro link de pago existente";
                } else if (
                  selectedId !== link.id &&
                  selectedFromDate <= link.from_date &&
                  selectedToDate >= link.from_date
                ) {
                  return "*El rango de fechas seleccionado no está disponible porque se superpone parcialmente con otro periodo existente";
                }
              }
              return true;
            },
          }}
          minimumDate={
            getValues("from_date")
              ? new Date(getValues("from_date"))
              : new Date()
          }
          maximumDate={
            selectedPaymentLink
              ? new Date(selectedPaymentLink?.expires_at)
              : null
          }
          disabled={disableForm}
        />
        <div className="flex-1 h-full w-full flex items-end justify-end pb-6">
          <div className="w-[15rem]">
            <PrimaryButton type="submit" disabled={disableForm} isFullWidth>
              {linkToUpdate ? "Actualizar link" : "Agregar link"}
              {!linkToUpdate && (
                <PlusIcon className="w-5 h-5 ml-2 inline-block" />
              )}
            </PrimaryButton>
          </div>
        </div>
      </form>
    </SlideOver>
  );
}

AgentPaymentLinkSlideForm.propTypes = {
  openSlideForm: PropTypes.bool.isRequired,
  setOpenSlideForm: PropTypes.func.isRequired,
  linkToUpdate: PropTypes.object,
  linksParams: PropTypes.array.isRequired,
  setLinksParams: PropTypes.func.isRequired,
};
